import { getValidAccessToken, recoverStaleAuthSession } from '../../../lib/supabase';
import type {
  PartnerCommissionRow,
  PartnerDashboardStats,
  PartnerReferralRow,
} from '../../../domain/partners/adminTypes';
import type { PartnerStatus } from '../../../domain/partners/types';

export class PartnerApiError extends Error {
  readonly isForbidden: boolean;
  readonly isNotPartner: boolean;

  constructor(message: string, options: { forbidden?: boolean; notPartner?: boolean } = {}) {
    super(message);
    this.name = 'PartnerApiError';
    this.isForbidden = options.forbidden ?? false;
    this.isNotPartner = options.notPartner ?? false;
  }
}

export type PartnerProfile = {
  id: string;
  code: string;
  status: PartnerStatus;
  commissionRate: number;
  companyName: string;
  connectOnboardingComplete: boolean;
  crmAccessGranted: boolean;
  createdAt: string;
};

export type PartnerDashboardPayload = {
  partner: PartnerProfile;
  referralLink: string | null;
  stats: PartnerDashboardStats;
  referrals: PartnerReferralRow[];
  commissions: PartnerCommissionRow[];
};

type PartnerApiPayload = {
  code?: string;
  error?: string;
  url?: string;
};

async function partnerFetch<T>(path: string, init?: RequestInit): Promise<T> {
  await recoverStaleAuthSession();
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`;
  const method = (init?.method || 'GET').toUpperCase();
  const hasBody = init?.body != null;

  const response = await fetch(url, {
    ...init,
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });

  let payload: PartnerApiPayload = {};
  try {
    payload = (await response.json()) as PartnerApiPayload;
  } catch {
    payload = {};
  }

  if (response.status === 404 && payload.code === 'NOT_PARTNER') {
    throw new PartnerApiError('Parceiro não encontrado', { notPartner: true });
  }
  if (response.status === 401) {
    throw new PartnerApiError('Usuário não autenticado. Faça login novamente.', { forbidden: true });
  }
  if (response.status === 403) {
    throw new PartnerApiError(payload.error || 'Sem permissão para o portal parceiro', { forbidden: true });
  }
  if (!response.ok) {
    throw new Error(payload.error || 'Erro na requisição do parceiro');
  }

  return payload as T;
}

export async function fetchPartnerDashboard(): Promise<PartnerDashboardPayload> {
  return partnerFetch<PartnerDashboardPayload>('partner-dashboard', { method: 'GET' });
}

export async function startPartnerConnectOnboarding(returnUrl: string, refreshUrl?: string): Promise<string> {
  await recoverStaleAuthSession();
  const payload = await partnerFetch<{ url: string }>('partner-connect-onboard', {
    method: 'POST',
    body: JSON.stringify({ returnUrl, refreshUrl: refreshUrl || returnUrl }),
  });
  if (!payload.url) {
    throw new Error('URL de onboarding não retornada');
  }
  return payload.url;
}
